import * as React from "react";
import { useState } from "react";
import { userDisplay, type AppUser } from "../../../services/userService";
import { resolvePeople } from "../shared/people";
import {
    BETEILIGTEN_LABELS,
    type BeteiligtenFeld as Gruppe,
} from "../detail/personenZeile";
import { RdPopover } from "../../redesign/table/RdPopover";
import { UserKreis } from "./VerlaufEintraege";
import { IcoPlus } from "./vg3Icons";
import "./vorgangV3.css";

/**
 * Beteiligte als Avatar-Stack; Klick öffnet die Verwaltung der Gruppen.
 *
 * Im Vorgangs-KOPF erscheint das Feld nur bei einem Vorgang OHNE To-dos
 * (PO 03.09.2026) — sobald es To-dos gibt, gehören die Beteiligten an das
 * jeweilige To-do und im Kopf wären sie eine zweite, konkurrierende Antwort
 * auf dieselbe Frage. Das entscheidet der Aufrufer, nicht diese Komponente.
 */
type Props = {
    users: AppUser[];
    canEdit: boolean;
    /** Sichtbare Gruppen in dieser Reihenfolge. */
    gruppen: Gruppe[];
    werte: Record<Gruppe, string[]>;
    onChange: (gruppe: Gruppe, next: string[]) => void;
    /** Persönlicher Vorgang: Beteiligte sind unveränderlich. */
    persoenlich?: boolean;
    /** Beschriftung des leeren Triggers, z. B. „hinzufügen". */
    leerLabel?: string;
    /** Bei leerem Einzelfeld die Personensuche direkt öffnen. */
    sucheBeiLeerOffen?: boolean;
    /** Optionale Überschrift im Popover. */
    popoverTitel?: string;
    /**
     * Kreise oben, das Wort klein DARUNTER (PO 09.09.2026: „Beteiligte und zu
     * informieren so machen: Kreise anzeigen und darunter der Text als
     * Legende, kleiner als jetzt").
     *
     * Ohne diese Form steht das Wort NEBEN den Avataren und in derselben
     * Größe wie die übrigen Zeilenbeschriftungen — im To-do-Kopf, wo links
     * schon Fristen stehen, sind das zwei Wörter zu viel für den Platz. Als
     * Legende darunter bleibt die Zuordnung erhalten und kostet keine Breite.
     */
    mitLegende?: boolean;
    /**
     * Durchmesser der Personenkreise in Pixeln. Standard 26 — das Maß der
     * To-do-Karten und des Anlege-Kastens.
     *
     * V4 setzt 29 (Mockup `.kopf`, Sanierung 13.09.2026): dort sind die Rollen
     * die einzige Angabe rechts in der Zeile und standen mit 26 px sichtbar
     * kleiner da als die K·R·V-Kreise links daneben. Die Größe kommt als PROP
     * und nicht aus dem CSS, weil `InitialsAvatar` Breite und Höhe als
     * Inline-Stil setzt — eine CSS-Regel käme dagegen nicht an. Das
     * Profilfoto bleibt (PO 13.09.2026); `InitialsAvatar` selbst wird nicht
     * angefasst, nur seine Größe gesagt.
     */
    kreisGroesse?: number;
};

const STACK_MAX = 6;

export const BeteiligteFeld: React.FC<Props> = ({
    users,
    canEdit,
    gruppen,
    werte,
    onChange,
    persoenlich = false,
    leerLabel = "Beteiligte",
    sucheBeiLeerOffen = false,
    popoverTitel,
    mitLegende = false,
    kreisGroesse,
}) => {
    // Dedupliziert über alle sichtbaren Gruppen.
    //
    // Der ERSTELLER steht bewusst nicht mit im Stack (PO 07.09.2026: „bei
    // Beteiligte den Ersteller nicht mehr anzeigen"). Wer einen Vorgang
    // angelegt hat, arbeitet deshalb nicht an ihm — als Avatar zwischen den
    // Beteiligten behauptete er genau das, und in der Zeile „Zu informieren"
    // stand er sogar dann, wenn dort niemand eingetragen ist.
    const keys: string[] = [];
    for (const g of gruppen) for (const k of werte[g] ?? []) if (!keys.includes(k)) keys.push(k);
    const personen = resolvePeople(keys, users);

    const stack = (
        <span className="vg3-stack">
            {personen.slice(0, STACK_MAX).map((p) => (
                <UserKreis key={p.key} name={p.name} initials={p.initials} userId={p.userId} size={kreisGroesse} />
            ))}
            {personen.length > STACK_MAX && (
                <span className="vg3-badge" style={{ marginLeft: 4 }}>
                    +{personen.length - STACK_MAX}
                </span>
            )}
        </span>
    );

    /**
     * Was IM Trigger steht. Mit Legende immer zweistöckig — auch leer, sonst
     * sprängen die beiden Gruppen im To-do-Kopf in der Höhe auseinander,
     * sobald in einer jemand steht.
     */
    const inhalt = mitLegende ? (
        <span className="vg3-stack-legende">
            {personen.length ? (
                stack
            ) : (
                <span className="vg3-stack-leer" aria-hidden>
                    <IcoPlus size={12} />
                </span>
            )}
            <span className="vg3-stack-wort">{leerLabel}</span>
        </span>
    ) : personen.length ? (
        stack
    ) : (
        <span className="vg3-tbtn">
            <IcoPlus size={12} /> {leerLabel}
        </span>
    );

    if (persoenlich) {
        return (
            <>
                {stack}
                <span className="vg3-leer">nur du — persönlicher Vorgang</span>
            </>
        );
    }

    if (!canEdit) {
        if (mitLegende) return inhalt;
        return personen.length ? stack : <span className="vg3-leer">niemand eingetragen</span>;
    }

    return (
        <RdPopover
            width={340}
            dialogLabel="Beteiligte verwalten"
            trigger={({ toggle, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    className="vg3-stack-btn"
                    onClick={toggle}
                    title="Beteiligte verwalten"
                    // Ohne festen Namen hieße der Knopf für Screenreader mal
                    // „Beteiligte", mal wie die zufällig erste Person im Stack.
                    aria-label="Beteiligte verwalten"
                >
                    {inhalt}
                </button>
            )}
        >
            {() => (
                // Das Popover rendert per Portal an `document.body` — also AUSSERHALB
                // des `.kvp-rd`-Wrappers der Seite. Ohne eigene Hülle griffe hier
                // weder eine `.kvp-rd .vg3-*`-Regel noch eine der Farbvariablen.
                <div className="kvp-rd vg3-personen-popover">
                    {popoverTitel && <div className="vg3-personen-popover-titel">{popoverTitel}</div>}
                    {gruppen.map((g) => (
                        <GruppenZeile
                            key={g}
                            gruppe={g}
                            users={users}
                            werte={werte[g] ?? []}
                            onChange={(next) => onChange(g, next)}
                            initialSucheOffen={sucheBeiLeerOffen && (werte[g] ?? []).length === 0}
                        />
                    ))}
                </div>
            )}
        </RdPopover>
    );
};

/** Eine Gruppe: Mitglieder als entfernbare Kreise + Suchfeld zum Hinzufügen. */
const GruppenZeile: React.FC<{
    gruppe: Gruppe;
    users: AppUser[];
    werte: string[];
    onChange: (next: string[]) => void;
    initialSucheOffen?: boolean;
}> = ({ gruppe, users, werte, onChange, initialSucheOffen = false }) => {
    const [suche, setSuche] = useState<string | null>(initialSucheOffen ? "" : null);
    const personen = resolvePeople(werte, users);

    const q = (suche ?? "").trim().toLowerCase();
    const treffer = suche == null
        ? []
        : users
              .filter((u) => !werte.includes(u.id))
              .filter((u) => !q || userDisplay(u).toLowerCase().includes(q) || (u.email ?? "").toLowerCase().includes(q))
              .slice(0, 6);

    return (
        <div className="vg3-personen-popover-gruppe">
            <div className="vg3-personen-popover-kopf">
                <span className="vg3-personen-popover-label">
                    {BETEILIGTEN_LABELS[gruppe]}
                </span>
                <button type="button" className="vg3-tbtn" onClick={() => setSuche(suche == null ? "" : null)}>
                    {suche == null ? "hinzufügen" : "fertig"}
                </button>
            </div>

            <div className="vg3-personen-popover-chips">
                {personen.length === 0 && <span className="vg3-leer">—</span>}
                {personen.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        className="vg3-slot"
                        title={`${p.name} entfernen`}
                        onClick={() => onChange(werte.filter((k) => k !== p.key))}
                    >
                        <UserKreis name={p.name} initials={p.initials} userId={p.userId} size={20} />
                        <span className="vg3-slot-t">{p.name}</span>
                        <span style={{ color: "var(--ink-3)" }}>×</span>
                    </button>
                ))}
            </div>

            {suche != null && (
                <div className="vg3-personen-suche">
                    <input
                        autoFocus
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                        placeholder="Name suchen…"
                        className="vg3-datum-in"
                        style={{ width: "100%" }}
                    />
                    <div className="vg3-personen-treffer">
                        {treffer.length === 0 && <span className="vg3-leer">keine Treffer</span>}
                        {treffer.map((u) => (
                            <button
                                key={u.id}
                                type="button"
                                className="vg3-tbtn"
                                style={{ textAlign: "left", fontWeight: 500, color: "var(--ink)" }}
                                onClick={() => {
                                    onChange([...werte, u.id]);
                                    setSuche("");
                                }}
                            >
                                {userDisplay(u)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
